import { toObservable } from '@angular/core/rxjs-interop';
import { Component, OnDestroy, OnInit, inject, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { IDocumentOnNode } from 'src/app/document-user/models/document.model';
import { getLinkToDocument } from 'src/app/document-user/functions/get-link-to-document';
import { LoadDocumentsAwaitingReadReceipt } from 'src/app/document-user/state/document.actions';
import { DocumentState } from 'src/app/document-user/state/document.state';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
} from 'src/app/shared/models/main-table-filter.model';
import { MainTableFilterOperand } from 'src/app/shared/main-table-module/main-table/main-table-column-filter/main-table-column-filter.component';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { DocumentReadReceiptsDashboardModalComponent } from './document-read-receipts-dashboard-modal.component';

const DASHBOARD_PAGE_SIZE = 10000;
const DASHBOARD_VISIBLE_DOCUMENTS_COUNT = 5;

@Component({
  selector: 'app-document-read-receipts-dashboard-widget',
  templateUrl: './document-read-receipts-dashboard-widget.component.html',
  styleUrls: ['./document-read-receipts-dashboard-widget.component.scss'],
  standalone: false,
})
export class DocumentReadReceiptsDashboardWidgetComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);

  documentsAwaitingReadReceipt = select(DocumentState.getDocumentsAwaitingReadReceipt);
  currentDepartmentId = select(TenantUserAccessState.currentDepartmentId);
  private readonly currentDepartmentId$ = toObservable(this.currentDepartmentId, {
    injector: this.injector,
  });
  displayedColumns = ['name', 'menuName'];
  columnTitlesMap = { name: 'document', menuName: 'placement' };
  loadingActions = [LoadDocumentsAwaitingReadReceipt];
  visibleDocumentsCount = DASHBOARD_VISIBLE_DOCUMENTS_COUNT;

  private subscription = new Subscription();

  constructor(
    private store: Store,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.currentDepartmentId$.subscribe((departmentId: string) => {
        this.handleDepartmentChange(departmentId);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openDocumentAwaitingReadReceipt(document: IDocumentOnNode): void {
    const { link, referrerType } = getLinkToDocument(document);
    this.router.navigate([link], { queryParams: { referrerType } });
  }

  onShowMoreDocumentsAwaitingReadReceipt(): void {
    this.dialog.open(DocumentReadReceiptsDashboardModalComponent, {
      data: {
        items: this.store.selectSnapshot(DocumentState.getDocumentsAwaitingReadReceipt),
      },
      width: '60rem',
      maxWidth: '90vw',
      panelClass: 'mobile-sizing-for-mat-dialogs',
    });
  }

  private handleDepartmentChange(departmentId: string): void {
    if (!departmentId) {
      return;
    }

    this.store.dispatch(
      new LoadDocumentsAwaitingReadReceipt(this.createDocumentsAwaitingReadReceiptFilter())
    );
  }

  private createDocumentsAwaitingReadReceiptFilter(): MainTableFilterModel {
    const filter = new MainTableFilterModel(MainTableFilterModulesEnum.DocumentsByMenu);
    filter.pageIndex = 0;
    filter.pageSize = DASHBOARD_PAGE_SIZE;
    filter.searchBy = [
      new MainTableFilterSearchByModel(
        'HasReadCurrentReadReceipt',
        false,
        MainTableFilterOperand.Equal
      ),
    ];

    return filter;
  }
}
